import React, { useEffect, useState } from 'react';
import { Typography, Button, Box, Card, Grid, MenuItem, Menu, IconButton, useTheme, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

import Breadcrumb from 'src/layouts/full/shared/breadcrumb/Breadcrumb';
import PageContainer from 'src/components/container/PageContainer';
import DataTable from '../../../components/DataTable/DataTable';
import { fdelete, fget, fput } from '../../../axios/axiosCalls';
import toast from 'react-hot-toast';
import ReactCountryFlag from 'react-country-flag';
import CountryBox from '../../../components/CountryBox';
import Chart from 'react-apexcharts';
import DashboardCard from '../../../components/shared/DashboardCard';
import { formatDateToYYYYMMDD } from '../../../utils/utils';
import { MoreVert, Search } from '@mui/icons-material';
import BookingEditDrawer from '../../../components/BookingEditDrawer';
import { debounce } from 'lodash';
import { Payment as PaymentIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';



const BCrumb = [
  {
    to: '/',
    title: 'Home',
  },
  {
    title: 'Bookings',
  },
];

const Bookings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCountryName, setSelectedCountryName] = useState(null);

  const [monthlyStats, setMonthlyStats] = useState([]);
  const [alltime, setAllTime] = useState(0)
  const [totalProperties, setTotalProperties] = useState(0)
  const [totalBeds, setTotalBeds] = useState(0)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSearch = debounce((query) => {
    setSearchQuery(query);
    setPage(0);
    fetchBookings();
  }, 500);
  const CancelBookingDialog = ({ open, onClose, onConfirm }) => {
    const [comment, setComment] = useState('');
  
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this booking? This action cannot be undone.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            type="text"
            fullWidth
            variant="outlined"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional comment for cancellation"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Keep Booking</Button>
          <Button onClick={() => onConfirm(comment)} color="error">
            Cancel Booking
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
  useEffect(() => {
      const fetchCountries = async () => {
        try {
          const response = await fget({url:'/api/v2/countries'});
          setCountries(response.data.countries);
          // setSelectedCountry(response.data.countries[0]?.id);
        } catch (error) {
          console.error('Error fetching countries:', error);
        }
      }
      fetchCountries();
    }, []);
    const fetchBookingStats = async () => {
      try {
        const response = await fget({ 
          url: '/api/v2/bookings/summary', 
          params: { countryId: selectedCountry === 'all' ? '' : selectedCountry } 
        });
    
        if (response.data.status === 'Success') {
          if (selectedCountry === 'all') {
            // Aggregate stats for all countries
            const allStats = response.data.bookings.reduce((acc, country) => {
              // Sum up totals
              acc.totalBookings = (acc.totalBookings || 0) + (country.totalBookings || 0);
              acc.totalProperties = (acc.totalProperties || 0) + (country.totalProperties || 0);
              acc.totalBeds = (acc.totalBeds || 0) + (country.totalBeds || 0);
    
              // Merge monthly bookings
              country.montlyBookings?.forEach(monthStat => {
                const existingMonth = acc.monthlyStats.find(m => m.month === monthStat.month);
                if (existingMonth) {
                  existingMonth.bookings = (parseInt(existingMonth.bookings) || 0) + (parseInt(monthStat.bookings) || 0);
                  existingMonth.bedsOccupied = (parseInt(existingMonth.bedsOccupied) || 0) + (parseInt(monthStat.bedsOccupied) || 0);
                } else {
                  acc.monthlyStats.push({
                    month: monthStat.month,
                    bookings: monthStat.bookings || 0,
                    bedsOccupied: monthStat.bedsOccupied || 0
                  });
                }
              });
              return acc;
            }, { monthlyStats: [] });
    
            // Sort monthly stats by date
            allStats.monthlyStats.sort((a, b) => {
              const [aMonth, aYear] = a.month.split('-');
              const [bMonth, bYear] = b.month.split('-');
              return new Date(aYear, aMonth - 1) - new Date(bYear, bMonth - 1);
            });
    
            // Update states with aggregated data
            setAllTime(allStats.totalBookings);
            setTotalProperties(allStats.totalProperties);
            setTotalBeds(allStats.totalBeds);
            setMonthlyStats(allStats.monthlyStats);
    
          } else {
            // Existing logic for single country
            const countryStats = response.data.bookings.find(
              booking => booking.countryId === selectedCountry
            );
            const stats = countryStats?.montlyBookings.map(stat => ({
              ...stat,
              bedsOccupied: stat.bedsOccupied || 0
            })) || [];
            
            setAllTime(countryStats?.totalBookings ?? 0);
            setTotalProperties(countryStats?.totalProperties ?? 0);
            setTotalBeds(countryStats?.totalBeds ?? 0);
            setMonthlyStats(stats);
          }
        }
      } catch (error) {
        console.error('Error fetching booking stats:', error);
        toast.error('Failed to fetch booking statistics');
      }
    };
    const fetchBookings = async () => {
      try {
        const response = await fget({ 
          url: '/api/v2/bookings', 
          params: { 
            countryId: selectedCountry === 'all' ? '' : selectedCountry,
            page: page + 1,
            limit: rowsPerPage,
            term: searchQuery
          } 
        });
        
        if (response.data.status === 'Success') {
          const bookings = response?.data?.bookings?.map((booking) => ({
            id: booking.bookingId,
            customer: booking.customer?.name,
            status: booking.bedType,
            country: selectedCountryName,
            startDate: booking.startDate,
            property: booking?.property?.propertyName,
            bedAllotted: booking?.bedAllotted,
            created_at: booking.createdAt,
            propertyId: booking?.property?.id,
            endDate: booking?.endDate,
            bedSpace: booking?.bedSpace,
            payment: booking?.payment,
            bookingEditRequest: booking?.bookingEditRequest,
            bookingStatus: booking?.bookingStatus
          }));
          setRows(bookings);

          setTotalRows(response.data?.pagination?.totalCount??0);
        } else {
          toast.error('Failed to fetch bookings');
        }
      } catch (error) {
        toast.error('An error occurred while fetching bookings');
        console.error('Fetch Bookings Error:', error);
      } finally {
        setLoading(false);
      }
    };
    const handlePageChange = (newPage) => {
      setPage(newPage);
    };
    
    const handleRowsPerPageChange = (newRowsPerPage) => {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    };
    
    useEffect(() => {
      if(!selectedCountry) return;
      fetchBookings();
      fetchBookingStats();
    }, [selectedCountry, page, rowsPerPage, searchQuery]);
    // Add ActionMenu component

const ActionMenu = ({ booking, onEdit }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const navigate = useNavigate();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    onEdit(booking);
    handleClose();
  };
  const handleCancelClick = () => {
    setCancelDialogOpen(true);
    handleClose();
  };

  const handleConfirmCancel = async (comment) => {
    try {
      const response = await fdelete({
        url: `/api/v2/bookings/${booking.id}/`,
        data: { comment }, // Pass comment in request body
      });
      
      if (response.data.status === 'Success') {
        toast.success('Booking Cancellation Request Created successfully');
        fetchBookings(); // Refresh the list
      } else {
        toast.error('Failed to create cancellation request');
      }
    } catch (error) {
      toast.error('An error occurred while cancelling booking');
      console.error('Cancel Booking Error:', error);
    }
    setCancelDialogOpen(false);
  };

  const handlePaymentClick = () => {
    navigate(`/property/${booking.propertyId}/payments/${booking.id}`);
    handleClose();
  };
  const hasDeleteRequest = booking?.bookingEditRequest?.some(request => request.requestType === 'delete');

  // Check if edit request exists  
  const hasEditRequest = booking?.bookingEditRequest?.some(request => request.requestType === 'edit');
  
  return (
    <>
    <IconButton onClick={handleClick}>
      <MoreVert />
    </IconButton>
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
    >
      {booking?.payment?.paymentStatus!=="success"&&<MenuItem onClick={handlePaymentClick} sx={{ 
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {/* <PaymentIcon fontSize="small" /> */}
          Make Payment
        </MenuItem>}
        {!hasEditRequest && <MenuItem onClick={handleEditClick}>Edit</MenuItem>}
        {!hasDeleteRequest && (
          <MenuItem onClick={handleCancelClick} sx={{ color: 'error.main' }}>
            Cancel Booking
          </MenuItem>
        )}
    </Menu>
    <CancelBookingDialog
      open={cancelDialogOpen}
      onClose={() => setCancelDialogOpen(false)}
      onConfirm={handleConfirmCancel}
    />
  </>
  );
};

// Inside Bookings component, add new states
const [drawerOpen, setDrawerOpen] = useState(false);
const [selectedBooking, setSelectedBooking] = useState(null);

// Add handler functions
const handleEdit = (booking) => {
  const bedSpaceId = booking.bedSpace?.replace(/[\[\]]/g, ''); // Remove brackets and get ID
  
  setSelectedBooking({
    ...booking,
    bedSpaceId: bedSpaceId // Add bedSpaceId to selectedBooking
  });
  setDrawerOpen(true);
};
const handleDelete = async (booking) => {
  try {
    const response = await fdelete({
      url: `/api/v2/bookings/${booking.id}/`
    });
    
    if (response.data.status === 'Success') {
      toast.success('Booking deleted successfully');
      fetchBookings(); // Refresh the table
    } else {
      toast.error('Failed to delete booking');
    }
  } catch (error) {
    toast.error('An error occurred while deleting booking');
    console.error('Delete Booking Error:', error);
  }
};
  const theme = useTheme();
  const primary = theme.palette.primary.main;
const handleSaveEdit = async (formData) => {
  try {
    const response = await fput({
      url: `/api/v2/bookings/${selectedBooking.id}`,
      data: {
        ...formData,
        startDate: formatDateToYYYYMMDD(formData.startDate),
        endDate: formatDateToYYYYMMDD(formData.endDate)
      }
    });
    
    if (response.data.status === 'Success') {
      toast.success('Booking update Request Submited successfully');
      setDrawerOpen(false);
      fetchBookings(); // Refresh the table
    } else {
      toast.error('Failed to update booking');
    }
  } catch (error) {
    toast.error('An error occurred while updating booking');
    console.error('Update Booking Error:', error);
  }
};
const columns = [
  { field: 'customer', headerName: 'Student', flex: 0.5 },
  // { field: 'country', headerName: 'Country', flex: 0.5 },
  { field: 'startDate', headerName: 'Start Date', flex: 0.5 },
  { field: 'property', headerName: 'Property', flex: 0.5 },
  { field: 'bedAllotted', headerName: 'Bed', flex: 0.5 },
  { field: 'bookingStatus', headerName: 'Status', flex: 0.5,
    renderCell: (params) => {
      return params.bookingStatus
      .replace('_', '')
      .charAt(0).toUpperCase() + 
      params.bookingStatus.replace('_', ' ').slice(1).toLowerCase();
    }
   },

  { field: 'payment.paymentStatus', headerName: 'Payment Status', flex: 0.5 ,
    renderCell: (params) => {
      return params.payment?.paymentStatus ;
    }
  },

  { field: 'created_at', headerName: 'Created', flex: 0.5,
    renderCell: (params) => {
      const dateObj = new Date(params.created_at);
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${day}-${month}-${year}`;
    },
    },


  { 
    field: 'actions', 
    headerName: 'Actions', 
    flex: 0.3,
    renderCell: (params) => <ActionMenu booking={params} onEdit={handleEdit} onDelete={handleDelete}/>
  },
  
];
  return (
    <PageContainer title="Bookings" description="Bookings">
    <Breadcrumb title="Bookings" items={BCrumb} />
    
    {/* Country Selection */}
    <Box sx={{
      mb: 3,
      display: 'flex',
      gap: 2,
      overflowX: 'auto',
      px: 1,
      py: 2,
      '&::-webkit-scrollbar': { height: 8 },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'grey.100',
        borderRadius: 4,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'grey.400',
        borderRadius: 4,
      },
    }}>
          <CountryBox
      key="all"
      country={{ 
        id: 'all',
        cname: 'All Countries',
        flag: '🌎'
      }}
      selected={selectedCountry === 'all'}
      onClick={() => {
        setSelectedCountry('all');
        setSearchQuery('');
        setSelectedCountryName('All Countries');
      }}
    />
      {countries.map((country) => (
        <CountryBox
          key={country.id}
          country={country}
          selected={country.id === selectedCountry}
          onClick={() => {setSelectedCountry(country.id);    setSearchQuery('');setSelectedCountryName(country.cname)}
          }
        />
      ))}
    </Box>

    {/* Stats and Chart Grid */}
    <Grid container spacing={3} sx={{ mb: 4, minHeight: 400 }}>
  <Grid item xs={12} md={9}>
    <DashboardCard sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom>Monthly Bookings</Typography>
      <Chart
        options={{
          chart: {
            type: 'line',
            toolbar: { show: false },
            foreColor: theme.palette.text.secondary
          },
          stroke: { 
            curve: 'smooth', 
            width: 2 
          },
          xaxis: {
            categories: monthlyStats.map(stat => stat.month),
            labels: {
              style: {
                colors: theme.palette.text.secondary,
                fontSize: '12px'
              }
            },
            axisBorder: {
              color: theme.palette.divider
            },
            axisTicks: {
              color: theme.palette.divider
            }
          },
          yaxis: {
            labels: {
              style: {
                colors: theme.palette.text.secondary,
                fontSize: '12px'
              }
            }
          },
          grid: {
            borderColor: theme.palette.divider,
            strokeDashArray: 4
          },
          colors: [theme.palette.primary.main],
          tooltip: {
            theme: theme.palette.mode,
            y: { 
              formatter: (val) => `${val} bookings`,
              style: {
                fontSize: '12px'
              }
            }
          },
          markers: {
            size: 4,
            colors: [theme.palette.primary.main],
            strokeColors: theme.palette.background.paper,
            strokeWidth: 2
          }
        }}
        series={[{
          name: 'Bookings',
          data: monthlyStats.map(stat => parseInt(stat.bookings))
        }]}
        type="line"
        height={250}
      />
    </DashboardCard>
  </Grid>
  
  <Grid item xs={12} md={3} sx={{ height: '400px' }}>
  <DashboardCard
    sx={{
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'space-between',
    }}
  >
    <Box sx={{  }}>
      <Typography variant="subtitle2" color="text.secondary">
        Total Bookings
      </Typography>
      <Typography variant="caption" color="text.secondary">
        All time
      </Typography>
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        my:'auto',  
        flex: 1,
        height: '80%',
        overflow: 'hidden',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        msOverflowStyle: 'none',  // IE and Edge
        scrollbarWidth: 'none'    // Firefox
      }}
    >
      <Typography variant="h2">{alltime}</Typography>
    </Box>
  </DashboardCard>
</Grid>
</Grid>

    {/* Bookings Table */}
      <Box sx={{ p: 0 }}>
      <Box sx={{ 
            mb: 3,
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <TextField
              placeholder="Search bookings..."
              onChange={(e) => debouncedSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{
                width: 300,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </Box>
      <DataTable 
          rows={rows} 
          columns={columns} 
          loading={loading}
          totalRows={totalRows}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
      <BookingEditDrawer
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      booking={selectedBooking}
      onSave={handleSaveEdit}
    />
  </PageContainer>
  );
};

export default Bookings;